const googleAnalyticsId = process.env.REACT_APP_GOOGLE_ANALYTICS_ID;
const metaPixelId = process.env.REACT_APP_META_PIXEL_ID;
const cookiebotId = process.env.REACT_APP_COOKIEBOT_ID;
const configuredSiteUrl = process.env.REACT_APP_SITE_URL;
const cookiebotScriptId = 'Cookiebot';
const forceCookieBanner = process.env.REACT_APP_FORCE_COOKIE_BANNER === 'true';
const isConsentDebugEnabled =
  process.env.NODE_ENV !== 'production' || process.env.REACT_APP_CONSENT_DEBUG === 'true';

let hasInitializedServices = false;
let hasInitializedGoogleAnalytics = false;

function logConsentDebug(eventName, payload = {}) {
  if (!isConsentDebugEnabled || typeof window === 'undefined') {
    return;
  }

  // Helpful during QA to verify consent state transitions and script loading order.
  // eslint-disable-next-line no-console
  console.info(`[consent] ${eventName}`, payload);
}

function trimTrailingSlash(value) {
  return value ? value.replace(/\/+$/, '') : '';
}

function getSiteUrl() {
  if (configuredSiteUrl) {
    return trimTrailingSlash(configuredSiteUrl);
  }

  if (typeof window !== 'undefined' && window.location?.origin) {
    return trimTrailingSlash(window.location.origin);
  }

  return '';
}

function getCurrentPageUrl() {
  if (typeof window === 'undefined') {
    return '';
  }

  const siteUrl = getSiteUrl();
  const pagePath = `${window.location.pathname}${window.location.search}`;
  return siteUrl ? `${siteUrl}${pagePath}` : window.location.href;
}

function loadScript(id, src, attributes = {}) {
  if (typeof document === 'undefined') {
    return;
  }

  if (document.getElementById(id)) {
    return;
  }

  const script = document.createElement('script');
  script.id = id;
  script.src = src;

  Object.entries(attributes).forEach(([key, value]) => {
    if (typeof value === 'boolean') {
      if (value) {
        script.setAttribute(key, '');
      }
      return;
    }

    if (value) {
      script.setAttribute(key, value);
    }
  });

  document.head.appendChild(script);
}

function initializeSeoMetadata() {
  if (typeof document === 'undefined') {
    return;
  }

  const pageUrl = getCurrentPageUrl();
  const canonicalLink = document.getElementById('canonical-link');
  const ogUrl = document.querySelector('meta[data-dynamic-og-url="true"]');

  if (canonicalLink && pageUrl) {
    canonicalLink.setAttribute('href', pageUrl);
  }

  if (ogUrl && pageUrl) {
    ogUrl.setAttribute('content', pageUrl);
  }
}

function ensureGtag() {
  window.dataLayer = window.dataLayer || [];

  if (!window.gtag) {
    window.gtag = function gtag() {
      window.dataLayer.push(arguments);
    };
  }
}

function updateGoogleConsent(consent) {
  if (!window.gtag) {
    return;
  }

  window.gtag('consent', 'update', {
    analytics_storage: consent.analytics ? 'granted' : 'denied',
    ad_storage: consent.marketing ? 'granted' : 'denied',
    ad_user_data: consent.marketing ? 'granted' : 'denied',
    ad_personalization: consent.marketing ? 'granted' : 'denied',
  });
}

function getCookiebotConsent() {
  if (!window.Cookiebot?.consent) {
    return null;
  }

  return {
    analytics: Boolean(window.Cookiebot.consent.statistics),
    marketing: Boolean(window.Cookiebot.consent.marketing),
  };
}

function initializeGoogleAnalyticsCore() {
  if (!googleAnalyticsId || hasInitializedGoogleAnalytics) {
    return;
  }

  hasInitializedGoogleAnalytics = true;

  ensureGtag();
  loadScript('google-analytics', `https://www.googletagmanager.com/gtag/js?id=${googleAnalyticsId}`, {
    async: true,
  });

  window.gtag('js', new Date());
  window.gtag('config', googleAnalyticsId, {
    anonymize_ip: true,
    page_location: getCurrentPageUrl(),
    page_path: typeof window !== 'undefined' ? window.location.pathname : '/',
    send_page_view: true,
  });

}

function initializeGoogleAnalytics() {
  if (!googleAnalyticsId) {
    return;
  }

  if (!cookiebotId) {
    initializeGoogleAnalyticsCore();
    return;
  }

  const consent = getCookiebotConsent();
  if (consent?.analytics) {
    initializeGoogleAnalyticsCore();
    updateGoogleConsent(consent);
  }
}

function injectMetaPixel() {
  if (!metaPixelId || window.fbq) {
    return;
  }

  (function loadMetaPixel(f, b, e, v, n, t, s) {
    if (f.fbq) {
      return;
    }
    n = f.fbq = function fbq() {
      if (n.callMethod) {
        n.callMethod.apply(n, arguments);
      } else {
        n.queue.push(arguments);
      }
    };
    if (!f._fbq) {
      f._fbq = n;
    }
    n.push = n;
    n.loaded = true;
    n.version = '2.0';
    n.queue = [];
    t = b.createElement(e);
    t.async = true;
    t.src = v;
    s = b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t, s);
  })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');

  window.fbq('init', metaPixelId);
  window.fbq('track', 'PageView');
}

function initializeMetaPixel() {
  if (!metaPixelId) {
    return;
  }

  if (!cookiebotId) {
    injectMetaPixel();
    return;
  }

  const consent = getCookiebotConsent();
  if (consent?.marketing) {
    injectMetaPixel();
  }
}

function synchronizeConsent() {
  const consent = getCookiebotConsent();
  if (!consent) {
    logConsentDebug('sync-skipped-no-consent');
    return;
  }

  logConsentDebug('sync', consent);

  if (consent.analytics) {
    initializeGoogleAnalyticsCore();
  }

  updateGoogleConsent(consent);

  if (consent.marketing) {
    injectMetaPixel();
  }
}

function initializeCookiebot() {
  if (!cookiebotId) {
    return;
  }

  const shouldForceBanner =
    forceCookieBanner ||
    (typeof window !== 'undefined' &&
      new URLSearchParams(window.location.search).get('cookiebot') === 'renew');

  loadScript(cookiebotScriptId, 'https://consent.cookiebot.com/uc.js', {
    async: true,
    'data-cbid': cookiebotId,
    'data-blockingmode': 'auto',
    type: 'text/javascript',
  });

  window.addEventListener('CookiebotOnConsentReady', synchronizeConsent);
  window.addEventListener('CookiebotOnAccept', synchronizeConsent);
  window.addEventListener('CookiebotOnDecline', synchronizeConsent);

  if (shouldForceBanner) {
    window.addEventListener('CookiebotOnConsentReady', () => {
      if (window.Cookiebot?.renew) {
        window.Cookiebot.renew();
      }
    });
  }

  window.addEventListener('CookiebotOnConsentReady', () => {
    logConsentDebug('CookiebotOnConsentReady', getCookiebotConsent() || {});
  });
  window.addEventListener('CookiebotOnAccept', () => {
    logConsentDebug('CookiebotOnAccept', getCookiebotConsent() || {});
  });
  window.addEventListener('CookiebotOnDecline', () => {
    logConsentDebug('CookiebotOnDecline', getCookiebotConsent() || {});
  });
}

export function initializeSiteServices() {
  if (typeof window === 'undefined' || hasInitializedServices) {
    return;
  }

  hasInitializedServices = true;

  initializeSeoMetadata();
  initializeCookiebot();
  initializeGoogleAnalytics();
  initializeMetaPixel();
}